/**
 * FacilityAssessmentForm — E17. Public NECS Facility Assessment capture.
 */
import { useState } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { CheckCircle2 } from 'lucide-react';
import { INDUSTRIES } from '../lib/industryData';

const API = process.env.REACT_APP_BACKEND_URL;
const GOLD = '#C9A962';
const inputCls = 'w-full rounded-md px-3 py-2.5 text-sm text-white bg-[#141414] border-2 border-[#3f3f46] focus:border-[#C9A962] focus:outline-none';
const labelCls = 'block text-[11px] font-bold uppercase tracking-widest mb-1.5 text-[#C9A962]';

const FIELDS = [
  ['company_name', 'Company / Facility Name *', 'text'],
  ['contact_name', 'Contact Name *', 'text'],
  ['email', 'Email *', 'email'],
  ['phone', 'Phone *', 'text'],
  ['square_footage', 'Square Footage *', 'number'],
  ['buildings', 'Buildings', 'number'],
  ['floors', 'Floors', 'number'],
  ['operating_hours', 'Operating Hours', 'text'],
  ['restrooms', 'Restrooms', 'number'],
  ['floor_types', 'Floor Types', 'text'],
  ['carpet_area_sqft', 'Carpet Area (sq ft)', 'number'],
  ['windows', 'Windows (scope)', 'text'],
  ['waste', 'Waste Requirements', 'text'],
  ['dumpsters', 'Dumpsters', 'number'],
  ['exterior_surfaces', 'Exterior Surfaces', 'text'],
  ['service_frequency', 'Desired Service Frequency', 'text'],
  ['traffic', 'Facility Traffic', 'text'],
  ['labor_notes', 'Labor Notes', 'text'],
  ['equipment_notes', 'Equipment Notes', 'text'],
  ['products_notes', 'Product Preferences', 'text'],
];

export default function FacilityAssessmentForm() {
  const [form, setForm] = useState({ business_type: 'office', recycling: false, special_requirements: '' });
  const [files, setFiles] = useState([]);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setError(''); setBusy(true);
    try {
      let photos = [];
      if (files.length) {
        const fd = new FormData();
        files.forEach((f) => fd.append('files', f));
        const up = await fetch(`${API}/api/commercial-quote/assessments/photos`, { method: 'POST', body: fd });
        const ud = await up.json();
        if (!up.ok) throw new Error(ud?.detail || 'Photo upload failed');
        photos = ud.photos || [];
      }
      const body = { ...form, photos };
      ['square_footage', 'buildings', 'floors', 'restrooms', 'carpet_area_sqft', 'dumpsters'].forEach((k) => {
        body[k] = parseInt(body[k], 10) || (k === 'buildings' || k === 'floors' ? 1 : 0);
      });
      const res = await fetch(`${API}/api/commercial-quote/assessments`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d?.detail?.[0]?.msg || d?.detail || 'Submission failed');
      setDone(true);
    } catch (e) { setError(String(e.message)); }
    setBusy(false);
  };

  if (done) {
    return (
      <Card className="p-8 text-center" style={{ backgroundColor: '#1f1f1f', border: `2px solid ${GOLD}` }} data-testid="fa-success">
        <CheckCircle2 className="h-10 w-10 mx-auto mb-3" style={{ color: GOLD }} />
        <p className="text-white font-bold">Assessment request received — an NECS Account Manager will schedule your facility walkthrough and build your proposal.</p>
      </Card>
    );
  }

  return (
    <Card className="p-6 sm:p-8" style={{ backgroundColor: '#1f1f1f', border: '2px solid #3f3f46' }} data-testid="facility-assessment-form">
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        <div>
          <label className={labelCls}>Business Type *</label>
          <select className={inputCls} value={form.business_type} onChange={(e) => setForm((f) => ({ ...f, business_type: e.target.value }))} data-testid="fa-business-type">
            {Object.entries(INDUSTRIES).map(([k, v]) => <option key={k} value={k}>{v.necs} — {v.label}</option>)}
          </select>
        </div>
        {FIELDS.map(([k, label, type]) => (
          <div key={k}>
            <label className={labelCls}>{label}</label>
            <input type={type} className={inputCls} value={form[k] ?? ''} onChange={(e) => setForm((f) => ({ ...f, [k]: e.target.value }))} data-testid={`fa-${k}`} />
          </div>
        ))}
        <div className="sm:col-span-2 lg:col-span-3">
          <label className={labelCls}>Special Requirements</label>
          <textarea rows={3} className={inputCls} value={form.special_requirements} onChange={(e) => setForm((f) => ({ ...f, special_requirements: e.target.value }))} data-testid="fa-special" />
        </div>
        <div className="sm:col-span-2 lg:col-span-3">
          <label className={labelCls}>Facility Photos (optional · JPG, PNG or PDF · max 5)</label>
          <input
            type="file" multiple accept=".jpg,.jpeg,.png,.pdf"
            onChange={(e) => setFiles(Array.from(e.target.files || []).slice(0, 5))}
            className="block w-full text-xs text-zinc-400 file:mr-3 file:px-4 file:py-2 file:rounded-full file:border-0 file:text-xs file:font-bold file:bg-[#C9A962] file:text-black file:cursor-pointer"
            data-testid="fa-photos-input"
          />
          {files.length > 0 && (
            <p className="text-[11px] mt-1.5" style={{ color: GOLD }} data-testid="fa-photos-selected">
              {files.length} file{files.length > 1 ? 's' : ''} attached: {files.map((f) => f.name).join(', ')}
            </p>
          )}
        </div>
        <label className={`flex items-center gap-2 px-4 py-2 rounded-full text-xs font-bold uppercase tracking-wider cursor-pointer border-2 self-end w-max ${form.recycling ? 'bg-[#C9A962] text-black border-[#C9A962]' : 'text-[#C9A962] border-[#C9A962]/50'}`} data-testid="fa-recycling">
          <input type="checkbox" className="hidden" checked={form.recycling} onChange={(e) => setForm((f) => ({ ...f, recycling: e.target.checked }))} />
          Recycling Program
        </label>
      </div>
      {error && <p className="mt-3 text-sm text-red-400" data-testid="fa-error">{error}</p>}
      <Button onClick={submit} disabled={busy || !form.company_name || !form.email || !form.square_footage}
        className="w-full mt-6 font-black uppercase tracking-widest py-6 text-base"
        style={{ backgroundColor: GOLD, color: '#1a1a1a', fontFamily: 'Barlow Condensed, sans-serif' }}
        data-testid="fa-submit-btn">
        {busy ? 'SUBMITTING…' : 'BUILD PROPOSAL'}
      </Button>
    </Card>
  );
}
